import React, { createContext, useContext, useState, useEffect } from 'react';
import { supabase } from '../config/supabase';
import { setCurrentUserId, setTrackingUserData } from '../utils/analytics';
import { GOOGLE_ADS_SCOPE, stashOAuthSignupContext } from '../utils/oauthSignup';
import { metaBmOAuthScopes } from '../constants/metaFormGate';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!supabase) {
      setLoading(false);
      return;
    }

    const clearBadSession = async () => {
      try {
        await supabase.auth.signOut({ scope: 'local' });
      } catch (_) {
        /* ignore */
      }
      localStorage.removeItem('access_token');
      setUser(null);
    };

    const getInitialSession = async () => {
      try {
        const { data: { session }, error: sessionError } = await supabase.auth.getSession();

        if (sessionError) {
          await clearBadSession();
          return;
        }

        if (session) {
          const { data: { user }, error: userError } = await supabase.auth.getUser();
          if (userError || !user) {
            await clearBadSession();
            return;
          }
        }

        if (session?.access_token) {
          localStorage.setItem('access_token', session.access_token);
        } else {
          localStorage.removeItem('access_token');
        }

        setUser(session?.user ?? null);
        if (session?.user) {
          setTrackingUserData({
            email: session.user.email,
            external_id: session.user.id,
            full_name: session.user.user_metadata?.full_name || session.user.user_metadata?.name,
            country: 'BR',
          });
        }
      } catch (error) {
        await clearBadSession();
      } finally {
        setLoading(false);
      }
    };

    getInitialSession();

    try {
      const { data: { subscription } } = supabase.auth.onAuthStateChange(
        async (event, session) => {
          if (session?.access_token) {
            localStorage.setItem('access_token', session.access_token);
          } else {
            localStorage.removeItem('access_token');
          }

          setUser(session?.user ?? null);
          if (session?.user) {
            setTrackingUserData({
              email: session.user.email,
              external_id: session.user.id,
              full_name: session.user.user_metadata?.full_name || session.user.user_metadata?.name,
              country: 'BR',
            });
          }
          setLoading(false);
        }
      );

      return () => subscription.unsubscribe();
    } catch (error) {
      setLoading(false);
    }
  }, []);

  // Ao voltar do segundo plano (comum no celular), re-sincroniza sessão e token.
  useEffect(() => {
    if (!supabase) return;
    const onVisibility = () => {
      if (document.visibilityState !== 'visible') return;
      supabase.auth.getSession().then(async ({ data: { session }, error }) => {
        if (error) {
          try {
            await supabase.auth.signOut({ scope: 'local' });
          } catch (_) {
            /* ignore */
          }
          localStorage.removeItem('access_token');
          setUser(null);
          return;
        }
        if (session?.access_token) {
          localStorage.setItem('access_token', session.access_token);
          setUser(session.user ?? null);
        }
      });
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, []);

  // signupContext (origin, idioma, país) fica guardado até o fim do cadastro,
  // porque o redirect do Google não devolve o state da página.
  // withGoogleAds: pede o escopo do Google Ads junto, para o onboarding listar
  // as contas sem a segunda tela de OAuth (/register e /acess pedem).
  const signInWithGoogle = async ({ signupContext, withGoogleAds = false } = {}) => {
    if (!supabase) {
      return { data: null, error: new Error('Supabase não configurado') };
    }
    try {
      stashOAuthSignupContext({ ...(signupContext || {}), withGoogleAds });
      const redirectUrl = `${process.env.REACT_APP_FRONTEND_URL || 'https://app.giobrain.com'}/auth/callback`;

      const options = {
        redirectTo: redirectUrl,
        queryParams: { prompt: 'select_account' },
      };
      if (withGoogleAds) {
        options.scopes = GOOGLE_ADS_SCOPE;
        options.queryParams.include_granted_scopes = 'true';
      }

      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options,
      });

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  };

  // withMetaAds: pede as permissões da Meta (as mesmas do FastConnect), para o
  // onboarding abrir direto na escolha de BM (/register e /acess pedem).
  const signInWithFacebook = async ({ signupContext, withMetaAds = false } = {}) => {
    if (!supabase) {
      return { data: null, error: new Error('Supabase não configurado') };
    }
    try {
      stashOAuthSignupContext({ ...(signupContext || {}), withMetaAds });
      const redirectUrl = `${process.env.REACT_APP_FRONTEND_URL || 'https://app.giobrain.com'}/auth/callback`;

      const options = { redirectTo: redirectUrl };
      if (withMetaAds) {
        options.scopes = metaBmOAuthScopes().join(',');
      }

      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: 'facebook',
        options,
      });

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  };

  const signOut = async () => {
    sessionStorage.removeItem('gio_overdue_invoice_popup');
    sessionStorage.removeItem('gio_overdue_invoice_stripe_return');
    if (!supabase) {
      localStorage.removeItem('access_token');
      setUser(null);
      return;
    }
    try {
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
    } catch (error) {
      // Silently handle logout errors
    } finally {
      localStorage.removeItem('access_token');
      sessionStorage.removeItem('gio_overdue_invoice_popup');
      sessionStorage.removeItem('gio_overdue_invoice_stripe_return');
      setUser(null);
    }
  };

  // Conversões (trial, compra, cadastro) buscam fbc e UTMs do cadastro no banco pelo user_id.
  useEffect(() => {
    setCurrentUserId(user?.id || null);
  }, [user?.id]);

  return (
    <AuthContext.Provider value={{ user, loading, signInWithGoogle, signInWithFacebook, signOut }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    const missing = async () => ({ data: null, error: new Error('AuthProvider não encontrado') });
    return { user: null, loading: true, signInWithGoogle: missing, signInWithFacebook: missing, signOut: async () => {} };
  }
  return context;
};
